import {
  Component, signal, effect, OnInit,
  inject, ChangeDetectionStrategy, DestroyRef,
  DOCUMENT
} from '@angular/core';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';
import { LucideAngularModule } from 'lucide-angular';

import { NavbarComponent }     from './sections/navbar/navbar.component';
import { HeroComponent }       from './sections/hero/hero.component';
import { BeneficiosComponent } from './sections/beneficios/beneficios.component';
import { PlanesComponent }     from './sections/planes/planes.component';
import { CoberturaComponent }  from './sections/cobertura/cobertura.component';
import { EmpresasComponent }   from './sections/empresas/empresas.component';
import { TestimoniosComponent }from './sections/testimonios/testimonios.component';
import { FaqComponent }        from './sections/faq/faq.component';
import { CtaFinalComponent }   from './sections/cta-final/cta-final.component';
import { FooterComponent }     from './sections/footer/footer.component';
import { ChatbotComponent }   from './chatbot/chatbot.component';

export const NAV_LINKS = [
  { label: 'Beneficios', href: '#beneficios' },
  { label: 'Planes',     href: '#planes'     },
  { label: 'Cobertura',  href: '#cobertura'  },
  { label: 'Empresas',   href: '#empresas'   },
  { label: 'FAQ',        href: '#faq'        },
];

@Component({
    selector: 'app-root',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        LucideAngularModule,
        NavbarComponent, HeroComponent, FooterComponent,
        BeneficiosComponent, PlanesComponent, CoberturaComponent,
        EmpresasComponent, TestimoniosComponent, FaqComponent, CtaFinalComponent,
        ChatbotComponent,
    ],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  private doc        = inject(DOCUMENT) as Document;
  private destroyRef = inject(DestroyRef);

  readonly navLinks  = NAV_LINKS;
  readonly portalUrl = 'https://clientes.chircalnetwifi.com.ve/';

  scrolled  = signal(false);
  isDark    = signal(false);
  menuOpen  = signal(false);

  constructor() {
    // Sync dark mode to document attribute
    effect(() => {
      this.doc.documentElement.setAttribute(
        'data-theme', this.isDark() ? 'dark' : 'light',
      );
    });

    // Navbar scroll state
    fromEvent(window, 'scroll', { passive: true })
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.scrolled.set(window.scrollY > 8));
  }

  ngOnInit() {
    this.setupParallax();
    this.setupMagnetic();
  }

  toggleDark()      { this.isDark.update(v => !v); }
  openMenu()        { this.menuOpen.set(true); }
  closeMenu()       { this.menuOpen.set(false); }

  private setupParallax() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    fromEvent<MouseEvent>(window, 'mousemove')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(ev => {
        const cx = window.innerWidth / 2, cy = window.innerHeight / 2;
        const dx = (ev.clientX - cx) / cx, dy = (ev.clientY - cy) / cy;
        this.doc.querySelectorAll<HTMLElement>('[data-parallax]').forEach(n => {
          n.style.transform = `translate3d(${dx * 14}px,${dy * 14}px,0)`;
        });
      });
  }

  private setupMagnetic() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    fromEvent<MouseEvent>(window, 'mousemove')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(ev => {
        this.doc.querySelectorAll<HTMLElement>('[data-magnetic]').forEach((b: HTMLElement) => {
          const r  = b.getBoundingClientRect();
          const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
          const dist = Math.hypot(ev.clientX - cx, ev.clientY - cy);
          b.style.transform = dist < 80
            ? `translate(${(ev.clientX - cx) * 0.25}px,${(ev.clientY - cy) * 0.35}px)`
            : '';
        });
      });
  }
}
